import dayjs from "utils/dayjs-config";
import type {
  CategorizedItems,
  GroupedRemaining,
  UpdatedItem,
  WorkflowItem
} from "./types";

export const WF_COMPLETED = "WF_COMPLETED";
export const WF_IN_PROGRESS = "IN_PROGRESS";

export const forEachRemainingWorkflowItem = (
  remaining: GroupedRemaining,
  callback: (item: WorkflowItem) => void
): void => {
  Object.values(remaining).forEach((milestones) => {
    Object.values(milestones).forEach((items) => {
      items.forEach(callback);
    });
  });
};

export const findRemainingWorkflowItem = (
  remaining: GroupedRemaining,
  itemId: string
): WorkflowItem | undefined => {
  let targetItem: WorkflowItem | undefined;
  forEachRemainingWorkflowItem(remaining, (item) => {
    if (item.id === itemId) {
      targetItem = item;
    }
  });
  return targetItem;
};

export const buildWorkflowItemUpdate = (
  item: WorkflowItem,
  actualDate: string
): UpdatedItem => ({
  feature_instance_id: item.id,
  workflow_instance_id: item.date_block.id,
  id: item.current_milestone_id as string,
  actualDate
});

export function truncateWithEllipsis(input: string, maxLength = 20): string {
  if (!input) return input;
  return input.length > maxLength
    ? `${input.substring(0, maxLength)}...`
    : input;
}

export const isDateValid = (
  selectedDate: string,
  previousMilestoneDate: string | null | undefined
): boolean => {
  if (!previousMilestoneDate || !selectedDate) return true;
  return dayjs(selectedDate).isSameOrAfter(dayjs(previousMilestoneDate), "day");
};

const hasPredecessors = (item: WorkflowItem): boolean =>
  item.has_predecessors ??
  ((item.linked_items?.length ?? 0) > 0 || (item.materials?.length ?? 0) > 0);

const isBlockedByPredecessors = (item: WorkflowItem): boolean =>
  hasPredecessors(item) &&
  item.ready_for_release === false &&
  item.workflow_status !== WF_IN_PROGRESS &&
  item.first_milestone_id === item.current_milestone_id;

/**
 * Splits selected items into:
 *  - completed: workflow complete OR no next milestone (cannot set an actual date)
 *  - predecessorsIncomplete: (only when enablePredecessorExclusion) linked items that
 *    are not yet ready for release, not force-released (IN_PROGRESS), and still
 *    sitting at the first milestone
 *  - remaining: editable items, grouped by template name -> next-milestone name
 */
export const categorizeItems = (
  items: WorkflowItem[],
  enablePredecessorExclusion = false
): CategorizedItems => {
  const completed: WorkflowItem[] = [];
  const predecessorsIncomplete: WorkflowItem[] = [];
  const remaining: GroupedRemaining = {};

  items.forEach((item) => {
    if (item.workflow_status === WF_COMPLETED || !item.current_milestone_id) {
      completed.push(item);
    } else if (enablePredecessorExclusion && isBlockedByPredecessors(item)) {
      predecessorsIncomplete.push(item);
    } else {
      const templateName = item.template?.name ?? "";
      const milestoneName = item.current_milestone_name as string;

      if (!remaining[templateName]) {
        remaining[templateName] = {};
      }
      if (!remaining[templateName][milestoneName]) {
        remaining[templateName][milestoneName] = [];
      }
      remaining[templateName][milestoneName].push(item);
    }
  });

  return { completed, predecessorsIncomplete, remaining };
};
